import { BoardInput, CollageInput, GameInput, Space, SpaceType, WallInput } from '../mainApi';
import { UploadUtils } from '../utils';
import { getDefaultVoteColors } from '../utils/voteColors';
import { ApiClientService } from './ApiClientService';

export class SpacesService {
  static canSetThumbnail(space: Space): boolean {
    return space.type === 'board' || space.type === 'wall';
  }

  static getThumbnailUrl(space: Space): string | null {
    if (!this.canSetThumbnail(space) || !space.thumbnail?.image) {
      return null;
    }

    const id = space.id ?? space._id ?? '';
    const resource = space.type === 'board' ? 'topics' : 'boards';
    const version = space.thumbnailCacheBust ?? space.thumbnail.image;

    return `${ApiClientService.getApiBaseUrl()}/${resource}/thumbnail/${id}?_v=${encodeURIComponent(
      String(version)
    )}`;
  }

  static async getAllBoards(): Promise<Space[]> {
    const spaces = await ApiClientService.getJson<Array<Omit<Space, 'type'>>>(
      `${ApiClientService.getApiBaseUrl()}/topics`
    );
    return spaces.map((space) => ({ ...space, type: 'board' }));
  }

  static async getAllWalls(): Promise<Space[]> {
    const spaces = await ApiClientService.getJson<Array<Omit<Space, 'type'>>>(
      `${ApiClientService.getApiBaseUrl()}/boards`
    );
    return spaces.map((space) => ({ ...space, type: 'wall' }));
  }

  static async getAllCollages(): Promise<Space[]> {
    const spaces = await ApiClientService.getJson<Array<Omit<Space, 'type'>>>(
      `${ApiClientService.getApiBaseUrl()}/collages`
    );
    return spaces.map((space) => ({ ...space, type: 'collage' }));
  }

  static async getAllGames(): Promise<Space[]> {
    const spaces = await ApiClientService.getJson<Array<Omit<Space, 'type'>>>(
      `${ApiClientService.getApiBaseUrl()}/games`
    );
    return spaces.map((space) => ({ ...space, type: 'game' }));
  }

  static async getAll(): Promise<Space[]> {
    const [boards, walls, collages, games] = await Promise.all([
      this.getAllBoards(),
      this.getAllWalls(),
      this.getAllCollages(),
      this.getAllGames(),
    ]);

    return [...boards, ...walls, ...collages, ...games];
  }

  private static getBoardPayload(input: BoardInput, source?: Space) {
    const uploadTypes = UploadUtils.normalizeUploadTypes(
      input.uploadTypes ??
        (source?.uploadTypes as string | Array<'text' | 'image' | 'video' | 'pdf'> | undefined)
    );
    const voteIconType =
      input.voteIconType ?? (source?.voteIconType as string | undefined) ?? 'heart';
    const defaultVoteColors = getDefaultVoteColors(voteIconType);

    return {
      isPublic: input.isPublic ?? (source?.isPublic as boolean | undefined) ?? true,
      workspace: input.workspace,
      title: input.title,
      description: input.description,
      password: input.password,
      votingStyle: input.votingStyle ?? (source?.votingStyle as string | undefined) ?? 'dotvoting',
      votesPerUser: input.votesPerUser ?? (source?.votesPerUser as number | undefined) ?? 3,
      layout: input.layout ?? (source?.layout as string | undefined) ?? 'grid',
      columns:
        input.columns ??
        (source?.columns as Array<{ id: string; title: string; sortNo: number }> | undefined) ??
        [],
      cardSorting: input.cardSorting ?? (source?.cardSorting as string | undefined) ?? 'automatic',
      voteIconType,
      myVoteColor:
        input.myVoteColor ??
        (source?.myVoteColor as string | undefined) ??
        defaultVoteColors.myVoteColor,
      theirVoteColor:
        input.theirVoteColor ??
        (source?.theirVoteColor as string | undefined) ??
        defaultVoteColors.theirVoteColor,
      uploadTypes: uploadTypes.join(','),
      randomizeCardPosition:
        input.randomizeCardPosition ??
        (source?.randomizeCardPosition as boolean | undefined) ??
        false,
      giphyEnabled: input.giphyEnabled ?? (source?.giphyEnabled as boolean | undefined) ?? true,
      cardsRequireApproval:
        input.cardsRequireApproval ??
        (source?.cardsRequireApproval as boolean | undefined) ??
        false,
      viewParticipantNames:
        input.viewParticipantNames ??
        (source?.viewParticipantNames as boolean | undefined) ??
        false,
      allViewParticipantNames:
        input.allViewParticipantNames ??
        (source?.allViewParticipantNames as boolean | undefined) ??
        false,
      authentication:
        input.authentication ?? (source?.authentication as string | undefined) ?? 'default',
      chatEnabled: input.chatEnabled ?? (source?.chatEnabled as boolean | undefined) ?? true,
      timerEnabled: input.timerEnabled ?? (source?.timerEnabled as boolean | undefined) ?? true,
      participantsEnabled:
        input.participantsEnabled ?? (source?.participantsEnabled as boolean | undefined) ?? true,
      participantUploadsEnabled:
        input.participantUploadsEnabled ??
        (source?.participantUploadsEnabled as boolean | undefined) ??
        true,
      includeWorkspaceLink:
        input.includeWorkspaceLink ??
        (source?.includeWorkspaceLink as boolean | undefined) ??
        false,
      locked: input.locked ?? (source?.locked as boolean | undefined) ?? false,
      votingEnabled: input.votingEnabled ?? (source?.votingEnabled as boolean | undefined) ?? true,
      privateVotingEnabled:
        input.privateVotingEnabled ??
        (source?.privateVotingEnabled as boolean | undefined) ??
        false,
      commentsEnabled:
        input.commentsEnabled ?? (source?.commentsEnabled as boolean | undefined) ?? true,
      externalImport:
        input.externalImport ?? (source?.externalImport as boolean | undefined) ?? false,
    };
  }

  private static getWallPayload(input: WallInput, source?: Space) {
    return {
      isPrivate: input.isPrivate ?? (source?.isPrivate as boolean | undefined) ?? false,
      canCollaborate:
        input.canCollaborate ?? (source?.canCollaborate as boolean | undefined) ?? true,
      canRejoin: input.canRejoin ?? (source?.canRejoinSessions as boolean | undefined) ?? true,
      selfEdit: input.selfEdit ?? (source?.canOnlyEditOwn as boolean | undefined) ?? true,
      showCardOwnerNames:
        input.showCardOwnerNames ?? (source?.showCardOwnerNames as boolean | undefined) ?? true,
      isPublic: input.isPublic ?? (source?.isPublic as boolean | undefined) ?? true,
      workspace: input.workspace,
      title: input.title,
      sortOrder: (source?.sortOrder as string | undefined) ?? 'asc',
      password: input.password,
      includeWorkspaceLink:
        input.includeWorkspaceLink ??
        (source?.includeWorkspaceLink as boolean | undefined) ??
        false,
      allowDrawingTools:
        input.allowDrawingTools ?? (source?.allowDrawingTools as boolean | undefined) ?? true,
      allowCardCreation:
        input.allowCardCreation ?? (source?.allowCardCreation as boolean | undefined) ?? true,
      allowClustering:
        input.allowClustering ?? (source?.allowClustering as boolean | undefined) ?? true,
      allowCardResizing:
        input.allowCardResizing ?? (source?.allowCardResizing as boolean | undefined) ?? true,
      allowCardLocking:
        input.allowCardLocking ?? (source?.allowCardLocking as boolean | undefined) ?? true,
      allowCardUnlocking:
        input.allowCardUnlocking ?? (source?.allowCardUnlocking as boolean | undefined) ?? true,
      allowAiTools: input.allowAiTools ?? (source?.allowAiTools as boolean | undefined) ?? true,
      enableProjectPlanning: (source?.enableProjectPlanning as boolean | undefined) ?? false,
      enableProjectPlanningSummary:
        (source?.enableProjectPlanningSummary as boolean | undefined) ?? false,
    };
  }

  static async create(
    spaceType: SpaceType,
    input: BoardInput | WallInput | CollageInput | GameInput
  ): Promise<{ path: string | null; space?: Space }> {
    if (spaceType === 'board') {
      const created = await ApiClientService.postJson<{ topic?: Space }>(
        `${ApiClientService.getApiBaseUrl()}/topics`,
        this.getBoardPayload(input as BoardInput)
      );

      return { path: `/b/${created.topic?.id ?? created.topic?._id ?? ''}`, space: created.topic };
    }

    if (spaceType === 'wall') {
      const created = await ApiClientService.postJson<Space>(
        `${ApiClientService.getApiBaseUrl()}/boards`,
        this.getWallPayload(input as WallInput)
      );
      return { path: `/w/${created.id ?? created._id ?? ''}`, space: created };
    }

    if (spaceType === 'game') {
      const created = await ApiClientService.postJson<Space>(
        `${ApiClientService.getApiBaseUrl()}/games`,
        {
          workspace: input.workspace,
          title: input.title,
        }
      );
      return { path: `/g/${created.id ?? created._id ?? ''}/cards`, space: created };
    }

    const collageInput = input as CollageInput;
    const response = await ApiClientService.postJson<{ collage?: Space } | Space>(
      `${ApiClientService.getApiBaseUrl()}/collages`,
      {
        isPublic: true,
        workspace: collageInput.workspace,
        title: collageInput.title,
        width: 800,
        height: 600,
        drawType: 'group',
        password: collageInput.password,
      }
    );
    const collage = (response as { collage?: Space }).collage ?? (response as Space);
    return { path: `/c/${collage.id ?? collage._id ?? ''}`, space: collage };
  }

  static async edit(
    space: Space,
    input: BoardInput | WallInput | CollageInput | GameInput
  ): Promise<void> {
    const id = space.id ?? space._id ?? '';

    if (space.type === 'board') {
      await ApiClientService.putJson(
        `${ApiClientService.getApiBaseUrl()}/topics/${id}`,
        this.getBoardPayload(input as BoardInput, space)
      );
      return;
    }

    if (space.type === 'wall') {
      await ApiClientService.putJson(
        `${ApiClientService.getApiBaseUrl()}/boards/${id}`,
        this.getWallPayload(input as WallInput, space)
      );
      return;
    }

    if (space.type === 'game') {
      await ApiClientService.putJson(`${ApiClientService.getApiBaseUrl()}/games/${id}`, {
        workspace: input.workspace,
        title: input.title,
      });
      return;
    }

    const collageInput = input as CollageInput;
    await ApiClientService.putJson(`${ApiClientService.getApiBaseUrl()}/collages/${id}`, {
      isPublic: (space.isPublic as boolean | undefined) ?? true,
      workspace: collageInput.workspace,
      title: collageInput.title,
      password: collageInput.password,
    });
  }

  static async copy(
    space: Space,
    input: BoardInput | WallInput | CollageInput | GameInput
  ): Promise<void> {
    const id = space.id ?? space._id ?? '';

    if (space.type === 'board') {
      await ApiClientService.postJson(
        `${ApiClientService.getApiBaseUrl()}/topics/copy/${id}`,
        this.getBoardPayload(input as BoardInput, space)
      );
      return;
    }

    if (space.type === 'wall') {
      await ApiClientService.postJson(
        `${ApiClientService.getApiBaseUrl()}/boards/${id}`,
        this.getWallPayload(input as WallInput, space)
      );
      return;
    }

    if (space.type === 'game') {
      throw new Error('Games cannot be copied yet.');
    }

    const collageInput = input as CollageInput;
    await ApiClientService.postJson(`${ApiClientService.getApiBaseUrl()}/collages/copy/${id}`, {
      isPublic: true,
      workspace: collageInput.workspace,
      title: collageInput.title,
      password: collageInput.password,
    });
  }

  static async archive(space: Space, archived: boolean): Promise<void> {
    const id = space.id ?? space._id ?? '';

    if (space.type === 'board') {
      await ApiClientService.putJson(`${ApiClientService.getApiBaseUrl()}/topics/archive/${id}`, {
        archived,
      });
      return;
    }

    if (space.type === 'wall') {
      await ApiClientService.putJson(`${ApiClientService.getApiBaseUrl()}/boards/archive/${id}`, {
        archived,
      });
      return;
    }

    if (space.type === 'game') {
      await ApiClientService.putJson(`${ApiClientService.getApiBaseUrl()}/games/archive/${id}`, {
        archived,
      });
      return;
    }

    await ApiClientService.putJson(`${ApiClientService.getApiBaseUrl()}/collages/archive/${id}`, {
      archived,
    });
  }

  static async remove(space: Space): Promise<void> {
    const id = space.id ?? space._id ?? '';

    if (space.type === 'board') {
      await ApiClientService.deleteJson(`${ApiClientService.getApiBaseUrl()}/topics/${id}`);
      return;
    }

    if (space.type === 'wall') {
      await ApiClientService.deleteJson(`${ApiClientService.getApiBaseUrl()}/boards/${id}`);
      return;
    }

    if (space.type === 'game') {
      await ApiClientService.deleteJson(`${ApiClientService.getApiBaseUrl()}/games/${id}`);
      return;
    }

    await ApiClientService.deleteJson(`${ApiClientService.getApiBaseUrl()}/collages/${id}`);
  }

  static async setThumbnail(space: Space, file: File): Promise<void> {
    if (!this.canSetThumbnail(space)) {
      throw new Error('Thumbnails are only supported for boards and whiteboards.');
    }

    const id = space.id ?? space._id ?? '';
    const resource = space.type === 'board' ? 'topics' : 'boards';
    const payload = new FormData();
    payload.append('file', file);

    await ApiClientService.postFormData(
      `${ApiClientService.getApiBaseUrl()}/${resource}/thumbnail/${id}/upload`,
      payload
    );
  }
}
